// Microsoft Clarity wrapper. The snippet itself lives in public/index.html
// and only loads on the production hostname (fitnessand.me). These helpers
// are safe to call from anywhere — they no-op when window.clarity is absent
// (preview, ad-blocked browsers, server-side renders, etc.).

type ClarityFn = {
  (cmd: 'event', name: string): void;
  (cmd: 'set', key: string, value: string | number | boolean): void;
  (cmd: 'identify', userId: string, sessionId?: string, pageId?: string, userHint?: string): void;
  (cmd: 'consent'): void;
  (cmd: 'upgrade', reason: string): void;
  (cmd: 'pause'): void;
  (cmd: 'resume'): void;
};

declare global {
  interface Window {
    clarity?: ClarityFn;
  }
}

function safe<T extends unknown[]>(fn: (...args: T) => void): (...args: T) => void {
  return (...args: T) => {
    try {
      if (typeof window === 'undefined' || !window.clarity) return;
      fn(...args);
    } catch {
      /* analytics must never crash the app */
    }
  };
}

/** Fire a named Clarity event. Use this to mark funnel steps in recordings. */
export const clarityEvent = safe<[string]>((name) => {
  window.clarity!('event', name);
});

/**
 * Attach a custom tag to the current session (e.g. plan='yearly').
 * Tags become filterable dimensions in the Clarity dashboard.
 */
export const claritySetTag = safe<[string, string | number | boolean]>((key, value) => {
  window.clarity!('set', key, value);
});

/**
 * Identify the logged-in user. We hash the email client-side so PII never
 * leaves the browser; the hash is a stable cross-session key for Clarity.
 */
export const clarityIdentify = safe<[string, Record<string, string | number | boolean> | undefined]>((userId, traits) => {
  window.clarity!('identify', userId);
  if (traits) {
    for (const k of Object.keys(traits)) {
      try { window.clarity!('set', k, traits[k]); } catch { /* noop */ }
    }
  }
});

/**
 * Stop / restart capture on SPA navigation. Clarity is only LOADED on the
 * public marketing surface (see the snippet in public/index.html), but a
 * visitor can log in without a page reload — pause there so the logged-in app
 * (workout logging, nutrition rings, charts) never pays the recording cost.
 */
export const clarityPause = safe<[]>(() => {
  window.clarity!('pause');
});

export const clarityResume = safe<[]>(() => {
  window.clarity!('resume');
});
